import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, finalize, takeUntil } from 'rxjs';
import { Distribution, ForecastEvaluationReport, ForecastOptions, ForecastReport, TaskPrediction } from '@contracts/interfaces/workload-forecast.interface';
import { WorkloadForecastService } from '../../services/workload-forecast.service';

@Component({
  selector: 'app-workload-forecast-tests',
  templateUrl: './workload-forecast-tests.component.html',
  styleUrls: ['./workload-forecast-tests.component.scss']
})
export class WorkloadForecastTestsComponent implements OnInit, OnDestroy {
  report: ForecastReport | null = null;
  baseline: ForecastReport | null = null;
  evaluation: ForecastEvaluationReport | null = null;
  loading = false;
  evaluationLoading = false;
  error = '';
  evaluationError = '';
  historyDays = 183;
  capacityPercent = 100;
  intakePercent = 100;
  pauseIntakeDays = 0;
  policy: ForecastOptions['selectionPolicy'] = 'balanced';
  priorityIds = '';
  taskQuery = '';
  selectedTask: TaskPrediction | null = null;
  private destroyed = new Subject<void>();

  constructor(private forecasts: WorkloadForecastService) {}
  ngOnInit(): void { this.loadBaseline(); }
  ngOnDestroy(): void { this.destroyed.next(); this.destroyed.complete(); }

  get filteredTasks(): TaskPrediction[] {
    const query = this.taskQuery.trim().toLowerCase();
    return (this.report?.taskForecasts || []).filter(task => !query || String(task.id).includes(query) || task.category.toLowerCase().includes(query));
  }
  get queriedExclusions() {
    const query = this.taskQuery.trim();
    return query ? (this.report?.excludedCurrentTasks || []).filter(row => String(row.id) === query) : [];
  }
  get weeklyMaximum(): number {
    return Math.max(1, ...(this.report?.historical.weekly || []).map(row => Math.max(row.created, row.completed)));
  }
  get comparison() {
    if (!this.baseline || !this.report || this.baseline.id === this.report.id || this.baseline.options.historyDays !== this.report.options.historyDays) return null;
    const before = this.baseline.projections.find(row => row.day === 30)?.outstanding.median;
    const after = this.report.projections.find(row => row.day === 30)?.outstanding.median;
    return before == null || after == null ? null : { before, after, delta: after - before, baselineAt: this.baseline.generatedAt };
  }
  get scenarioLabel(): string {
    const options = this.report?.options;
    if (!options) return '';
    return `${Math.round(options.capacityMultiplier * 100)}% capacity · ${Math.round(options.intakeMultiplier * 100)}% intake · ${options.pauseIntakeDays}-day intake pause · ${options.selectionPolicy}`;
  }
  get excludedReasons() {
    const counts = new Map<string, number>();
    for (const row of this.report?.excludedCurrentTasks || []) counts.set(row.reason, (counts.get(row.reason) || 0) + 1);
    return [...counts].map(([reason, count]) => ({ reason, count }));
  }
  number(value: number | null | undefined, digits = 1): string {
    return value == null ? 'Unavailable' : value.toLocaleString('en-AU', { maximumFractionDigits: digits });
  }
  percent(value: number | null | undefined): string { return value == null ? 'Unavailable' : `${this.number(value * 100, 0)}%`; }
  range(value: Distribution): string { return value.sampleSize ? `${this.number(value.p10)} – ${this.number(value.p90)}` : 'Unavailable'; }
  days(value: number | null): string {
    return value === null ? this.report?.state === 'computed' ? `Beyond ${this.report.horizonDays} days` : 'Unavailable' : `${this.number(value, 0)} days`;
  }
  loadBaseline(refresh = false): void {
    if (this.loading) return;
    this.historyDays = 183; this.capacityPercent = 100; this.intakePercent = 100;
    this.pauseIntakeDays = 0; this.policy = 'balanced'; this.priorityIds = '';
    this.load({}, refresh, true);
  }
  run(refresh = false): void {
    if (this.loading) return;
    const tokens = this.priorityIds.trim() ? this.priorityIds.trim().split(/[\s,]+/) : [];
    if (tokens.length > 100 || tokens.some(token => !/^\d+$/.test(token) || !Number.isSafeInteger(Number(token)) || Number(token) <= 0)) {
      this.error = 'Enter positive task IDs separated by commas or spaces.'; return;
    }
    if (![this.capacityPercent, this.intakePercent].every(value => Number.isFinite(value) && value >= 0 && value <= 300) ||
      !Number.isInteger(this.pauseIntakeDays) || this.pauseIntakeDays < 0 || this.pauseIntakeDays > 60) {
      this.error = 'Capacity and intake must be 0–300%. Intake pause must be a whole number from 0–60 days.'; return;
    }
    this.load({ historyDays: this.historyDays, capacityMultiplier: this.capacityPercent / 100,
      intakeMultiplier: this.intakePercent / 100, pauseIntakeDays: this.pauseIntakeDays,
      selectionPolicy: this.policy, priorityTaskIds: [...new Set(tokens.map(Number))] }, refresh, false);
  }
  loadEvaluation(): void {
    if (this.evaluationLoading) return;
    this.evaluationLoading = true; this.evaluationError = '';
    this.forecasts.evaluation().pipe(takeUntil(this.destroyed), finalize(() => this.evaluationLoading = false)).subscribe({
      next: report => this.evaluation = report,
      error: error => this.evaluationError = error.error?.error || 'Could not load forecast evaluation.'
    });
  }
  private load(options: Partial<ForecastOptions>, refresh: boolean, baseline: boolean): void {
    this.loading = true; this.error = '';
    const selectedId = this.selectedTask?.id ?? 1169;
    this.forecasts.get(options, refresh).pipe(takeUntil(this.destroyed), finalize(() => this.loading = false)).subscribe({
      next: report => {
        this.report = report;
        if (baseline) this.baseline = report;
        this.selectedTask = report.taskForecasts.find(task => task.id === selectedId) || report.taskForecasts[0] || null;
      },
      error: error => this.error = error.error?.error || 'Could not load the forecast. Check your connection and retry.'
    });
  }
}
